import React, { useMemo, useState } from 'react';
import DuplicateBadge from './DuplicateBadge';

function formatBytes(size) {
  const n = Number(size) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function memberKey(m) {
  return m.source === 'imap'
    ? `imap:${m.accountEmail || ''}:${m.folder}:${m.uid}`
    : `mongo:${m.mailId}`;
}

function MemberRow({ member, label, selected, onToggle, onOpen }) {
  return (
    <div className={`dup-member ${member.isOriginal ? 'is-original' : ''}`}>
      <label className="dup-member-check">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(member)}
          disabled={member.isOriginal}
        />
      </label>
      <div className="dup-member-body">
        <div className="dup-member-top">
          <span className="dup-member-label">{label}</span>
          <span className="dup-member-subject">{member.subject || '(No subject)'}</span>
        </div>
        <div className="dup-member-meta">
          <span>{member.from || '—'}</span>
          <span>{formatDate(member.date)}</span>
          <span>{member.folder || '—'}</span>
          <span>{member.accountEmail || 'Local'}</span>
          <span>{formatBytes(member.size)}</span>
          <span>
            {member.attachmentCount ? `${member.attachmentCount} file(s)` : 'No attachments'}
          </span>
        </div>
      </div>
      <button type="button" className="dup-icon-btn" title="Open" onClick={() => onOpen(member)}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
      </button>
    </div>
  );
}

export default function DuplicateGroupCard({
  group,
  checked,
  onCheckChange,
  onDeleteSelected,
  onCleanup,
  onIgnore,
  onNotDuplicate,
  onOpenMail,
}) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [menuOpen, setMenuOpen] = useState(false);

  const selectable = useMemo(
    () => (group.members || []).filter((m) => !m.isOriginal),
    [group.members]
  );

  const original = group.original || group.members?.[0];
  const dupCount = Math.max(0, (group.memberCount || group.members?.length || 0) - 1);
  const totalSize = (group.members || []).reduce((sum, m) => sum + (m.size || 0), 0);
  const latestDate = (group.members || []).reduce((best, m) => {
    const t = m.date ? new Date(m.date).getTime() : 0;
    return t > best ? t : best;
  }, 0);

  const toggle = (member) => {
    const key = memberKey(member);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedMembers = (group.members || []).filter((m) => selected.has(memberKey(m)));
  const snippet = original?.preview || original?.subject || 'No preview available';

  return (
    <article className={`dup-row-card ${expanded ? 'is-expanded' : ''} ${checked ? 'is-checked' : ''}`}>
      <div className="dup-row-main">
        <label className="dup-row-check">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onCheckChange?.(e.target.checked)}
          />
        </label>

        <div className="dup-row-avatar" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
          </svg>
          {dupCount > 0 && <span className="dup-row-count-badge">{dupCount + 1}</span>}
        </div>

        <button type="button" className="dup-row-content" onClick={() => setExpanded((v) => !v)}>
          <div className="dup-row-subject-line">
            <span className="dup-row-subject">{original?.subject || '(No subject)'}</span>
            <DuplicateBadge matchType={group.matchType} confidence={group.confidence} />
          </div>
          <div className="dup-row-from">{original?.from || 'Unknown sender'}</div>
          <div className="dup-row-snippet">{snippet}</div>
        </button>

        <div className="dup-row-stats">
          <span className="dup-row-dup-count">{dupCount} duplicates</span>
          <span className="dup-row-size">{formatBytes(totalSize)}</span>
          <span className="dup-row-date">{latestDate ? formatDate(latestDate) : '—'}</span>
        </div>

        <div className="dup-row-actions">
          <button
            type="button"
            className="dup-icon-btn"
            title="Preview original"
            onClick={() => original && onOpenMail(original)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
            </svg>
          </button>
          <button
            type="button"
            className="dup-icon-btn"
            title="Keep newest"
            onClick={() => onCleanup(group, 'keep_newest')}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
            </svg>
          </button>
          <div className="dup-more-wrap">
            <button
              type="button"
              className="dup-icon-btn"
              title="More"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
            {menuOpen && (
              <div className="dup-more-menu">
                <button type="button" onClick={() => { setMenuOpen(false); onCleanup(group, 'keep_oldest'); }}>Keep oldest</button>
                <button type="button" onClick={() => { setMenuOpen(false); onCleanup(group, 'keep_starred'); }}>Keep starred</button>
                <button type="button" onClick={() => { setMenuOpen(false); onCleanup(group, 'keep_unread'); }}>Keep unread</button>
                <button type="button" onClick={() => { setMenuOpen(false); onIgnore(group); }}>Ignore group</button>
                <button type="button" onClick={() => { setMenuOpen(false); onNotDuplicate(group); }}>Not a duplicate</button>
                <button
                  type="button"
                  className="danger"
                  disabled={!selectedMembers.length && !selectable.length}
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteSelected(group, selectedMembers.length ? selectedMembers : selectable);
                  }}
                >
                  Delete duplicates
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`dup-icon-btn dup-expand-btn ${expanded ? 'open' : ''}`}
            title={expanded ? 'Collapse' : 'Expand'}
            onClick={() => setExpanded((v) => !v)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="dup-row-details">
          {original && (
            <MemberRow
              member={original}
              label="Original"
              selected={false}
              onToggle={() => {}}
              onOpen={onOpenMail}
            />
          )}
          {(group.duplicates || []).map((dup, idx) => (
            <MemberRow
              key={memberKey(dup)}
              member={dup}
              label={`Duplicate #${idx + 1}`}
              selected={selected.has(memberKey(dup))}
              onToggle={toggle}
              onOpen={onOpenMail}
            />
          ))}
          <div className="dup-row-detail-actions">
            <button
              type="button"
              className="dup-danger-btn"
              disabled={!selectedMembers.length}
              onClick={() => onDeleteSelected(group, selectedMembers)}
            >
              Delete selected
            </button>
            <button type="button" className="dup-chip-btn" onClick={() => onCleanup(group, 'keep_newest')}>
              Keep newest
            </button>
            <button type="button" className="dup-chip-btn" onClick={() => onCleanup(group, 'keep_oldest')}>
              Keep oldest
            </button>
            <button type="button" className="dup-link-btn" onClick={() => onNotDuplicate(group)}>
              Not a duplicate
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
